/**
 * Internal-traffic flag — when true, every pixel wrapper (GA4, Meta, UET,
 * LinkedIn, HubSpot, PostHog) short-circuits.
 *
 * WHY IT PERSISTS. The flag can only be set once `currentUser.isAdmin` comes
 * back from GraphQL — one round-trip AFTER hydration. Everything that fires in
 * that window was captured as ordinary traffic, and for PostHog it was worse
 * than a few events: `posthog.init()` had already run, and posthog-js
 * autocapture ($autocapture / $pageview / $web_vitals / $dead_click /
 * $rageclick / $pageleave / $exception) runs INSIDE the SDK, where this flag
 * cannot reach it. Measured 2026-07-31: 29,684 of 631,687 events over 30 days
 * (4.7%) came from 5 staff members — 14.9% of all `$autocapture`.
 *
 * So the verdict is cached in localStorage and re-hydrated synchronously on
 * the FIRST `isInternalTraffic()` call of every subsequent page load. Because
 * `posthog.init()` checks this flag before doing anything, a known-staff
 * browser never initializes posthog-js at all — which is the only way to stop
 * autocapture at the source. Same early-hydration benefit for GA4/Meta/UET.
 *
 * Cost: the very first session on a new browser/profile still leaks until the
 * admin's identity resolves. That is unavoidable without blocking analytics on
 * an auth round-trip for every visitor, and PostHog's `test_account_filters`
 * (`$is_internal_user`, `email icontains @spotme.mx`) covers the residue.
 *
 * Self-healing: a NON-admin authenticating on the same browser clears the key
 * (`clearBrowserInternal`), so a shared or handed-down machine cannot silently
 * suppress a real user's analytics forever.
 */
import { shouldSuppressCapabilityTelemetry } from 'src/utils/capabilityPrivacy'
import {
  safeStorageGet,
  safeStorageRemove,
  safeStorageSet
} from 'src/utils/safeStorage'

const INTERNAL_TRAFFIC_STORAGE_KEY = 'spotme:internal-traffic'

let _isInternal = false
let _hydrated = false

/**
 * Read the cached verdict once per page load. Lazy rather than module-scope so
 * it never runs during SSR, and so callers need no ordering discipline — the
 * first guard to ask is the one that pays for it.
 */
const hydrate = (): void => {
  if (_hydrated) return
  if (typeof window === 'undefined') return
  _hydrated = true
  if (safeStorageGet('local', INTERNAL_TRAFFIC_STORAGE_KEY) === '1') {
    _isInternal = true
  }
}

/**
 * In-memory only — does NOT touch the cached verdict. Logout calls this for
 * every user, and an admin logging out has not stopped being staff, so the
 * browser stays flagged until a non-admin actually signs in.
 */
export const setInternalTraffic = (value: boolean): void => {
  _hydrated = true
  _isInternal = value
}

/** Flag this browser as staff for all future page loads. */
export const markBrowserInternal = (): void => {
  setInternalTraffic(true)
  safeStorageSet('local', INTERNAL_TRAFFIC_STORAGE_KEY, '1')
}

/** Positive evidence a non-admin uses this browser — drop the cached verdict. */
export const clearBrowserInternal = (): void => {
  setInternalTraffic(false)
  safeStorageRemove('local', INTERNAL_TRAFFIC_STORAGE_KEY)
}

export const isInternalTraffic = (): boolean => {
  hydrate()
  // All pixel wrappers share this gate, including calls from auth providers.
  // Capability privacy is transient and never sets the persisted staff flag.
  return shouldSuppressCapabilityTelemetry() || _isInternal
}
